import { useCallback } from "react";

import { Element } from "@craftjs/core";
import { ArrowUpDown } from "lucide-react";

import { define, prop, useCraftProp } from "@/components/builder/utils";
import { useNavigationStore, NavigationProvider as NavigationProviderStore } from "@/modules/cms/views/setting/views/navigation/store";

import { NavigationItemBlock } from "..";
import BasePreview from "../base";

import { NavigationProvider } from "./context";

function NavigationPreview({ properties, properties: { navigationId } = {}, ...props }) {
  const { collection: { item: navigation } } = useNavigationStore()

  if (!navigationId) {
    return <BasePreview properties={properties} {...props}>
      <div>Select a navigation</div>
    </BasePreview>
  }

  return <BasePreview properties={properties} {...props}>
    <ul>
      {navigation?.cmsNavItems?.map(item => <Element id={`navigation-item-${item.id}`} is={NavigationItemBlock} key={`navigation-item-${item.id}`} label={item.label} />)}
    </ul>
  </BasePreview>
}

function NavigationHTML({ navigationId, attributes, children }) {
  const [, slug] = navigationId?.split(':') ?? []

  return <>
    <ul {...attributes}>
      {`{{#navigation "${slug}"}}`}
      {children[0]}
      {`{{/navigation}}`}
    </ul>
  </>
}

export function useNavigationOptions() {
  const { collections: { items } = {} } = useNavigationStore()

  return useCallback(async () => ({
    options: items.map(({ id, slug, name }) => ({
      value: `${id}:${slug}`,
      label: `[#${id}] ${name}`,
    })),
  }), [items])
}

function BlockContext({ children }) {
  const { value: navigationId } = useCraftProp('navigationId')
  const { collection: { useLoad } } = useNavigationStore()

  const navigationIdValue = navigationId?.split(':')?.[0]

  useLoad(navigationIdValue)

  return <NavigationProvider>
    {children}
  </NavigationProvider>
}

function PropertiesContext({ children }) {
  const { collections: { useLoad } } = useNavigationStore()

  useLoad({ autoRefresh: false })
  return children
}

export default define('navigation', NavigationPreview, NavigationHTML)
  .wrap(({ children }) => <NavigationProviderStore><BlockContext>{children}</BlockContext></NavigationProviderStore>)
  .context(({ children }) => <NavigationProviderStore><PropertiesContext>{children}</PropertiesContext></NavigationProviderStore>)
  .palete(<ArrowUpDown size={20} />, 'Navigation', 'Embedded')
  .level('site')
  .property(prop.stylePattern())
  .property(prop.select.async('Navigation', 'navigationId').category('default').ignoreDevice(true).own()
    .props({
      useOptions: useNavigationOptions,
      clearable: true,
    }))
  .property(prop.predefined.customClass())
  .property(...prop.predefined.typography())
  .property(...prop.predefined.borders())
  .property(...prop.predefined.appearance())
  .property(...prop.predefined.layout())
  .defaultProps({
    size: {
      width: 'w-fit',
    },
  })
  .build()
